// components/header/HeaderActions.tsx
import { ActionIcon, Group, Indicator } from "@mantine/core";
import { IconLogin, IconLogout, IconShoppingCart } from "@tabler/icons-react";

import { authApi } from "api/common.service";
import { useNavigate } from "react-router-dom";
import { useCartStore } from "stores/useCartStore";
import { useSessionStore } from "stores/userSessionStore";
import classes from "./DefaultHeader.module.css";
import SwitchSystem from "./SwitchSystem";
import SwitchTheme from "./SwitchTheme";

// Define the props for the HeaderActions component
// This interface defines the structure of the props that the HeaderActions component will receive
interface HeaderActionsProps {
  onLoginClick: () => void;
  onCartClick?: () => void;
}

// The HeaderActions component is a functional component that renders action icons for login/logout, mode selection, and site switching
// It uses Mantine components for styling and layout, and it utilizes the useAuth context to manage authentication state
// The component accepts a prop for handling login click events
export default function HeaderActions({
  onLoginClick,
  onCartClick,
}: HeaderActionsProps) {
  // Destructure the isLoggedIn and setLoggedIn from the useAuth context
  // This allows the component to access the authentication state and update it
  const { isLoggedIn, isGlobalAdmin } = useSessionStore();
  const navigate = useNavigate();

  const cartItemCount = useCartStore(
    (state) => state.cart?.items?.reduce((sum, i) => sum + i.quantity, 0) ?? 0,
  );
  const handleSignOut = async () => {
    await authApi.post("/users/signOut");
    useSessionStore.getState().clearUser(); // if you’re storing session info locally
  };

  return (
    <Group>
      {isLoggedIn ? (
        <Group gap="xs">
          <ActionIcon variant="light" onClick={handleSignOut}>
            <IconLogout title="Sign me out! 🛡️" size={20} />
          </ActionIcon>
          <Indicator
            disabled={cartItemCount === 0}
            label={`${cartItemCount > 9 ? "9+" : cartItemCount}`}
            size={16}
            offset={6}
          >
            <ActionIcon variant="light" onClick={onCartClick}>
              <IconShoppingCart
                title="View Cart 🛒"
                onClick={() => navigate("/cart")}
                size={20}
              />
            </ActionIcon>
          </Indicator>
        </Group>
      ) : (
        <ActionIcon variant="light" onClick={onLoginClick}>
          <IconLogin title="Sign me in! 🛡️" size={20} />
        </ActionIcon>
      )}
      <SwitchTheme />
      <SwitchSystem />
      {isGlobalAdmin && (
        <a
          key="playground"
          href="/playpen"
          className={classes.link}
          onClick={(event) => {
            navigate("/playpen"); // Use history to navigate to the link
            event.preventDefault();
          }}
        >
          Playground
        </a>
      )}
    </Group>
  );
}
